import React, { ChangeEvent, memo, useCallback } from 'react';
import styled, { css } from 'styled-components';

type StyledRadioProps = {
	radioId: string;
	label?: string;
	fontColor?: string;
	fontFamily?: string;
	margin?: string;
	name: string;
	value?: any;
	checked?: any;
	dataValue?: string;
	disabled?: boolean;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const StyledRadio = ({
	radioId,
	label,
	margin,
	name,
	value,
	checked,
	onChange,
	dataValue = '',
	disabled = false
}: StyledRadioProps) => {
	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		if (!disabled && onChange) {
			onChange(e);
		}
	}, [disabled, onChange]);

	return (
		<Wrapper $margin={margin}>
            <Label htmlFor={radioId} $disabled={disabled} $checked={value === checked}>
                <Marker $disabled={disabled} $checked={value === checked} />
                <Input
	                id={radioId}
	                type="radio"
	                name={name}
	                value={value}
	                // 선택 상태를 화면 표시와 같은 값으로 맞춘다 (비워 두면 행이 지워지거나 바뀐 뒤 이전 선택이 남아, 같은 항목을 눌러도 onChange 가 오지 않는다)
	                checked={value === checked}
	                onChange={handleChange}
	                data-value={dataValue}
	                disabled={disabled}
	                aria-label={label || '라디오 버튼'}
                />
	            {label && <Text>{label}</Text>}
            </Label>
        </Wrapper>
	);
};

StyledRadio.displayName = 'StyledRadio';

export default memo(StyledRadio);

const Wrapper = styled.div<{ $margin?: string; }>`
  display: flex;
  align-items: center;
  margin: ${({ $margin }) => $margin ?? 0};
`;

const Label = styled.label<{ $disabled?: boolean; $checked?: boolean; }>`
  color: ${({ theme }) => theme.colors.deepGrayFontColor};
  display: flex;
  align-items: center;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  transition: opacity 0.2s ease;
  
  ${({ $checked, theme }) => $checked && css`
  	color: ${theme.colors.blackColor};
  `};
  
  
`;

const Marker = styled.div<{ $checked: boolean; $disabled?: boolean; }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGrayBorder};
  background-color: ${({ theme }) => theme.colors.ultraLightGrayBorder};
  transition: border-color 0.35s, background-color 0.35s;

  ${({ $checked }) => $checked && css`
    border: 5px solid ${({ theme }) => theme.colors.activeBlue};
    background-color: ${({ theme }) => theme.colors.whiteColor};
  `};

  ${({ $disabled }) => $disabled && css`
    opacity: 0.5;
  `};
`;

const Input = styled.input`
  display: none;
`;

const Text = styled.div`
  margin-left: 12px;
  padding-bottom: 2px;
`;
